import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, MapPin } from "lucide-react";
import { StoryActions } from "../components/StoryActions";
import { GalleryCarousel } from "../components/GalleryCarousel";
import { Seo } from "../components/Seo";
import { ShareButton } from "../components/ShareButton";
import { LOGO, PHOTO_GRID } from "../lib/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { n: 1, title: "Kies je gerechten" },
  { n: 2, title: "Kies dag & tijd" },
  { n: 3, title: "Wij koken, jij haalt op" },
];

const FeaturedTori = () => {
  const [tori, setTori] = useState(null);
  useEffect(() => {
    axios.get(`${API}/tori/featured`).then((r) => setTori(r.data)).catch((e) => console.error("Featured Tori laden mislukt:", e));
  }, []);
  if (!tori) return null;
  const photos = tori.photos || [];
  const place = tori.locality || tori.location_text;
  return (
    <section className="bg-white py-20" data-testid="tori-van-de-week">
      <div className="max-w-[1200px] mx-auto w-full px-6">
        <div className="flex items-center justify-center gap-2 mb-10">
          <Star size={26} className="text-[#F4D03F] fill-[#F4D03F]" />
          <h2 className="text-3xl md:text-4xl font-[700] text-[#0D3B4C] text-center">Tori van de week</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {photos.length > 0 && (
            <div className={`grid gap-2 ${photos.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
              {photos.slice(0, 4).map((p) => (
                <div key={p} className={`overflow-hidden bg-[#E8F5E9] ${photos.length === 1 ? "aspect-[4/3]" : "aspect-square"}`}>
                  <img
                    src={`${BACKEND}${p}`}
                    alt={`Tori van de week: ${tori.name}${place ? " uit " + place : ""} — Surinaamse gerechten`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-col">
            <h3 className="text-2xl font-[700] text-[#0D3B4C]">{tori.name}</h3>
            {place && (
              <p className="font-[600] text-[#1B6B2E] flex items-center gap-1 mt-1">
                <MapPin size={16} /> {place}
              </p>
            )}
            <p className="text-base font-[400] text-[#0D3B4C] mt-4 leading-relaxed">{tori.story}</p>
            <div className="mt-6">
              <StoryActions id={tori.id} likes={tori.likes} />
            </div>
            <Link
              to="/verhalen"
              data-testid="featured-view-all"
              className="mt-6 self-start bg-[#B71C1C] text-white font-[700] uppercase tracking-wider py-3 px-8 rounded-full hover:bg-[#8E1515] transition-colors"
            >
              Bekijk alle verhalen
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const HomeGallery = () => {
  const [slides, setSlides] = useState(null);
  useEffect(() => {
    axios.get(`${API}/gallery`)
      .then((r) => {
        const items = (r.data || []).map((g) => ({
          src: g.url?.startsWith("http") ? g.url : `${BACKEND}${g.url}`,
          caption: g.caption || "",
        }));
        setSlides(items);
      })
      .catch(() => setSlides([]));
  }, []);

  // Fallback to the built-in stock photos until the admin uploads their own.
  const finalSlides = slides === null
    ? []
    : slides.length > 0
      ? slides
      : PHOTO_GRID.map((src) => ({ src, caption: "" }));

  if (slides === null || finalSlides.length === 0) return null;

  return (
    <section className="bg-[#FEF9E7] py-20" data-testid="home-gallery">
      <div className="max-w-[1200px] mx-auto w-full px-6">
        <div className="overflow-hidden shadow-[0_20px_50px_-20px_rgba(13,59,76,0.4)]">
          <GalleryCarousel slides={finalSlides} />
        </div>
      </div>
    </section>
  );
};


export default function Home() {
  return (
    <div>
      <Seo
        title="Zeeuwse Tori | Authentieke Surinaamse Keuken"
        description="Ontdek de lekkerste thuisgemaakte Surinaamse gerechten in Zeeland. Bestel direct online en haal op in Middelburg."
        image="og-image.jpg"
        path="/"
        business
      />
      {/* Hero */}
      <section className="relative w-full min-h-[85vh] md:min-h-[92vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background photo — cover full viewport on every screen size */}
        <div
          className="absolute inset-0 bg-no-repeat bg-center bg-cover"
          style={{ backgroundImage: "url('/hero-bg.jpg')", backgroundColor: "#7A5230" }}
          role="img"
          aria-label="Authentieke Surinaamse gerechten van De Zeeuwse Tori in verpakking met logo op het deksel"
          data-testid="hero-background"
        />
        {/* Readability overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D3B4C]/75 via-[#0D3B4C]/45 to-[#0D3B4C]/80" />
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-5 sm:px-6 py-12 md:py-16 gap-6 md:gap-8">
          <div
            className="animate-badge-in w-72 h-72 md:w-[26rem] md:h-[26rem] rounded-full flex flex-col items-center justify-center shadow-2xl"
            style={{ background: "radial-gradient(circle at center, #FFFFFF 55%, #E8F5E9 100%)" }}
            data-testid="hero-badge"
          >
            <img src={LOGO} alt="De Zeeuwse Tori logo — authentiek home-made Surinaams eten in Zeeland" className="w-56 h-56 md:w-80 md:h-80 rounded-full object-cover" />
          </div>
          <div className="animate-fade-up bg-white/40 backdrop-blur-lg rounded-[2.5rem] px-7 sm:px-10 py-10 md:py-12 flex flex-col items-center justify-center gap-8 md:gap-10 max-w-[720px] w-full shadow-[0_25px_60px_-20px_rgba(0,0,0,0.55)] border border-white/40" style={{ animationDelay: "0.4s" }}>
            <h1 className="text-[1.24rem] sm:text-[1.35rem] md:text-[1.375rem] font-[700] text-[#1B6B2E] tracking-wide whitespace-normal md:whitespace-nowrap [text-shadow:0_1px_4px_rgba(255,255,255,0.6)]">
              Authentiek home-made Surinaams eten
            </h1>
            <div className="flex flex-col items-center gap-4">
              <Link
                to="/menu-samenstellen"
                data-testid="hero-cta"
                className="bg-[#B71C1C] text-white font-[700] text-base md:text-lg uppercase tracking-wider py-4 px-10 rounded-full shadow-lg hover:bg-[#8E1515] transition-colors [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]"
              >
                Honger? Bestel hier!
              </Link>
              <ShareButton slug="home" title="De Zeeuwse Tori" label="Deel deze pagina" className="inline-flex items-center gap-2 text-[#0D3B4C] font-[700] text-sm uppercase tracking-wider hover:text-[#1B6B2E] transition-colors" />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#FEF9E7] py-20" data-testid="how-it-works">
        <div className="max-w-[1200px] mx-auto w-full px-6 flex flex-col items-center">
          <h2 className="text-3xl md:text-4xl font-[700] text-[#0D3B4C] mb-12 text-center">Hoe het werkt</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 w-full">
            {steps.map((s) => (
              <div key={s.n} className="flex flex-col items-center text-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#F4D03F] text-[#0D3B4C] font-[700] text-xl flex items-center justify-center">
                  {s.n}
                </div>
                <p className="text-lg md:text-xl font-[600] text-[#0D3B4C]">{s.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tori van de week */}
      <FeaturedTori />

      {/* Homepage gallery carousel (managed in admin) */}
      <HomeGallery />
    </div>
  );
}
